// ============================================================================
// DECK BUILDER — pitchDeckBuilder.jsx
// ============================================================================
// Comms → Pitches → a deck. The reviewer's screen for one comms.pitch_decks
// row (docs/pitch-deck-generator-plan.md, section 7.3):
//   header   title, pitch type, quality, status, suggest again, prepare images,
//            preview and render
//   left     chapters with ordered projects, include toggles, confidential
//            override, hero and supporting image pickers, reasons
//   right    sections in order, the intro text, and the list of projects the
//            engine left out and why
//
// Edits write to the deck row at once and bump the tab's data revision, so a
// reload shows the same state. Chapters and sections are also kept in local
// state so drags feel instant.
// ============================================================================

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { supabase } from '../../supabaseClient';
import { DndContext, closestCenter, SortableContext, verticalListSortingStrategy, arrayMove } from './pitchDnd';
import { GripVertical, Trash2, ImagePlus, RefreshCw, AlertTriangle, FileDown } from 'lucide-react';
import { commsUpdate, commsDelete } from './commsUtils';
import { callEdgeFunction, removeTagFromIds, updateAssetTags } from './pressAssets';
import { S, GOLD, Chip, BlockTitle, FilterBtn, NoteArea, InlineInput, AddLine, BtnGhost, BtnPrimary, BackBtn, EmptyNote, statusClass } from './commsUi';
import { MONO, selectStyle, iconBtn, SortableRow, useDragSensors, Modal, SectionsEditor, Stars, categoriesFrom } from './pitchUi';
import { QUALITY_TIERS, PITCH_TAGS, FEE_BANDS, ATTACHMENT_MAX_BYTES, newId, readDeckChapters, readDeckSections } from '../../pitch/pitchSections';
import { assemblePitchRender, buildPitchHtml, isLeadershipRole } from '../../pitch/renderPitchHtml';
import { buildDraftDeck, inquiryProfile, projectAssets, pickImages, resolveAssetId, scoreProject, DEFAULT_EUR_INR } from '../../pitch/selectPitchContent';

const cap = s => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const now = () => new Date().toISOString();
// Thumbnail tiers a rendered deck reads from, one per slot the renderer has:
// `l` fills a whole 1920 px page, `m` a framed picture inside a half (at most
// 720 px wide), `s` one of the small gallery frames (at most 460 px). Sending
// the largest everywhere made a forty spread deck a 43 MB PDF and pushed
// Chromium's print stage past the function's time limit, because the print
// cost tracks image bytes almost exactly. See src/pitch/renderPitchHtml.js.
const RENDER_THUMB_SIZES = { l: 'w2048h1536', m: 'w1024h768', s: 'w640h480' };
// Only a full page bleed needs the largest tier, and only a project's lead
// image is ever used that way, so the rest of the deck never pays for it.
const FRAME_THUMB_SIZES = [RENDER_THUMB_SIZES.m, RENDER_THUMB_SIZES.s];
const HERO_THUMB_SIZES = [RENDER_THUMB_SIZES.l];
// Portraits are shown three or four to a half, so a couple of hundred pixels
// wide. The grid tier is already more than that.
const PORTRAIT_THUMB_SIZE = RENDER_THUMB_SIZES.s;
// Presentation tier: originals downscaled to 3840 px by /api/pitch-image and
// cached in the private pitch-images bucket (plan, section 5.3). The builder
// signs the cached objects for a week, so a deck prepared longer ago than
// that is stale even without an edit.
const PRESENTATION_WIDTH = 3840;
// Merge tiers into whatever a file id already carries, so a second listing
// (the lead images' large tier) adds to the first rather than replacing it.
// `thumb_urls` is only sent by a listing function that understands several
// sizes; while that deploy is behind the app, the single `thumb_url` comes
// back at the first size asked for, so it is read as that tier rather than
// thrown away.
const tiersFrom = (entry, sizes) => {
  const byTag = entry.thumb_urls || (entry.thumb_url ? { [sizes[0]]: entry.thumb_url } : {});
  const out = {};
  Object.entries(RENDER_THUMB_SIZES).forEach(([key, tag]) => { if (byTag[tag]) out[key] = byTag[tag]; });
  return out;
};
const mergeTiers = (existing, add) => {
  const base = existing && typeof existing === 'object' ? existing : (typeof existing === 'string' ? { m: existing } : {});
  return { ...base, ...add };
};
const SIGNED_URL_SECONDS = 7 * 24 * 3600;
const SIGNED_URL_STALE_MS = 6 * 24 * 3600 * 1000;
// Derivations run a few at a time: each one downloads a full original.
const PRESENTATION_PARALLEL = 3;
// No network call is allowed to hang forever. A thumbnail listing that never
// answers used to leave the progress state set, which silently turned the
// Render PDF button into a no-op with nothing on screen to explain it.
const FOLDER_TIMEOUT_MS = 120000;
// File ids per lookup. Each one costs Dropbox a metadata call plus a share of
// a thumbnail batch, so a whole deck in one request would sit near the timeout
// while telling the reviewer nothing.
const RESOLVE_BATCH = 40;
// The picker's tiles are 150 px wide, so the grid tier is plenty.
const PREVIEW_THUMB_SIZE = 'w480h320';
const RENDER_TIMEOUT_MS = 180000;

async function withTimeout(promise, ms, what) {
  let timer;
  try {
    return await Promise.race([
      promise,
      new Promise((_, reject) => { timer = setTimeout(() => reject(new Error(`${what} did not answer within ${Math.round(ms / 1000)} seconds`)), ms); }),
    ]);
  } finally { clearTimeout(timer); }
}
// Team portraits live in Dropbox under this tree, one folder per member
// (supabase/functions/_shared/teamPortraits.ts). The member folder holds the
// current portrait, its Originals subfolder the uncropped upload ("Source")
// and earlier portraits. The deck takes the uncropped upload when there is
// one, because the app's crop is small and the layout crops to 4 by 5 itself.
const PORTRAITS_BASE = '/Architecture Brio/BRIO-press/00_Practice/Portraits & Team/Portraits';
const ORIGINALS_FOLDER = 'Originals';
const fmtMb = n => `${(n / 1048576).toFixed(1)} MB`;

// ── Image picker ─────────────────────────────────────────────────────────────

function Thumb({ asset, size = 56 }) {
  const h = Math.round(size * 0.7);
  if (asset && asset.thumb_url) return <img src={asset.thumb_url} alt={asset.file_name || ''} style={{ width: size, height: h, objectFit: 'cover', borderRadius: 2, display: 'block', background: '#E6E4DD' }} />;
  return (
    <div title={asset ? asset.file_name : 'No image'} style={{ width: size, height: h, borderRadius: 2, background: '#E6E4DD', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9A988F', fontFamily: MONO, fontSize: 8, padding: 2, overflow: 'hidden', textAlign: 'center' }}>
      {asset ? (asset.file_name || '').slice(0, 24) : '—'}
    </div>
  );
}

// The picker opens on the images a deck would actually be built from, with
// everything else a click away. Four stars is the floor the engine itself
// treats as pitchable (pickImages fills a spread from five then four star
// images once the tagged ones run out), so it is the same line here.
const PICKER_MIN_RANK = 4;
const PICKER_FILTERS = [
  { key: 'shortlist', label: `${PICKER_MIN_RANK}\u2605+` },
  { key: 'tagged', label: 'Tagged' },
  { key: 'all', label: 'All' },
];
const isPitchTagged = a => a.tags.includes(PITCH_TAGS.hero) || a.tags.includes(PITCH_TAGS.supporting);

// The chip the tile's three toggles share: filled when the thing is on,
// outlined when it is off. One shape for all of them, so the hero control and
// the two hashtags read as the same kind of switch rather than the hero one
// reading as a caption, which is what the grey text button it replaced did.
const chipStyle = (on, colour, busy) => ({
  fontFamily: MONO, fontSize: 9, letterSpacing: '0.04em', lineHeight: 1.6, padding: '1px 7px',
  borderRadius: 2, whiteSpace: 'nowrap', cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.55 : 1,
  border: `1px solid ${on ? colour : 'rgba(27,27,25,.22)'}`,
  background: on ? colour : 'transparent', color: on ? '#fff' : '#6E6C66',
});

// This deck's lead image. It sits with the stars rather than with the
// hashtags because it is a decision about this deck, not about the
// photograph; the hashtags below steer every deck. Shown on the current hero
// too, where clicking it hands the choice back to the engine — the tile you
// look at to change the hero used to be the one tile with no control on it.
function HeroChip({ isHero, canEdit, fileName, onToggle }) {
  if (!canEdit) return null;
  return (
    <button type="button" onClick={e => { e.stopPropagation(); onToggle(); }}
      title={isHero ? `Clear the lead image, currently ${fileName}` : `Use ${fileName} as the lead image`}
      style={chipStyle(isHero, GOLD)}>
      HERO
    </button>
  );
}

// One hashtag on one photograph, in the shared chip shape, so the tile says
// what could be tagged and not only what is. Without edit rights it falls back
// to the read-only badge this replaced.
function TagChip({ tag, label, colour, on, busy, canEdit, fileName, onToggle }) {
  if (!canEdit) return on ? <span style={{ fontFamily: MONO, fontSize: 9, color: colour }}>{label}</span> : null;
  return (
    <button type="button" disabled={busy} onClick={e => { e.stopPropagation(); onToggle(tag); }}
      title={on ? `Remove ${label} from ${fileName}` : `Tag ${fileName} ${label}`}
      style={{ ...chipStyle(on, colour, busy), flex: 1, padding: '1px 4px' }}>
      {label}
    </button>
  );
}

export function ImagePickerModal({ project, entry, assets, imagesPerProject, canEdit, onSave, onClose, onTagsChanged, showToast }) {
  // A deck built before the copies of a photograph were collapsed can hold the
  // id of a copy that is no longer the one on offer. Read through to the
  // photograph, so the reviewer sees their own selection rather than a blank
  // grid, and saving writes the id that is now used.
  const [hero, setHero] = useState(() => resolveAssetId(assets, entry.hero_file_id));
  const [ids, setIds] = useState(() => Array.from(new Set(entry.image_file_ids.map(id => resolveAssetId(assets, id)))));
  const [filter, setFilter] = useState('shortlist');
  // Hashtag edits made here, before the page's next fetch brings them back in
  // the assets prop: file id to the tags it now carries. The write is global
  // (comms.asset_tags), so it is what every later deck's Suggest will see.
  const [tagEdits, setTagEdits] = useState({});
  const [tagBusy, setTagBusy] = useState({});
  const tagsWritten = useRef(false);
  // A star written before the press browser recorded thumbnails leaves the
  // tile grey, and a grid of grey tiles cannot be chosen from. Ask for the
  // previews by file id, which is the one handle that survives a folder
  // rename. Cheap after the first time: Dropbox thumbnails are cached.
  const [found, setFound] = useState({});
  const [findingPreviews, setFindingPreviews] = useState(false);
  useEffect(() => {
    const missing = assets.filter(a => !a.thumb_url).map(a => a.file_id);
    if (!missing.length) return undefined;
    let live = true;
    setFindingPreviews(true);
    (async () => {
      try {
        for (let i = 0; i < missing.length && live; i += RESOLVE_BATCH) {
          const res = await withTimeout(
            callEdgeFunction('list-press-folder-contents', { fileIds: missing.slice(i, i + RESOLVE_BATCH), thumbs: true, thumbSize: PREVIEW_THUMB_SIZE }),
            FOLDER_TIMEOUT_MS, 'The preview lookup',
          );
          if (!live) return;
          const add = {};
          (res.entries || []).forEach(e => { if (e.file_id && e.thumb_url) add[e.file_id] = e.thumb_url; });
          if (Object.keys(add).length) setFound(cur => ({ ...cur, ...add }));
        }
      } catch { /* previews are a convenience; the names still identify the files */ }
      if (live) setFindingPreviews(false);
    })();
    return () => { live = false; };
  }, [assets]);
  const sorted = useMemo(
    () => assets.map(a => ({
      ...a,
      tags: tagEdits[a.file_id] || a.tags,
      thumb_url: a.thumb_url || found[a.file_id] || null,
    })).sort((a, b) => b.rank - a.rank || a.file_name.localeCompare(b.file_name)),
    [assets, found, tagEdits],
  );
  // The deck's own picks are never filtered away, or narrowing the grid would
  // hide the spread you are composing and the count below would not add up.
  const visible = useMemo(() => sorted.filter(a => (
    a.file_id === hero || ids.includes(a.file_id)
    || filter === 'all'
    || (filter === 'tagged' ? isPitchTagged(a) : a.rank >= PICKER_MIN_RANK)
  )), [sorted, filter, hero, ids]);
  const counts = useMemo(() => ({
    all: sorted.length,
    tagged: sorted.filter(isPitchTagged).length,
    shortlist: sorted.filter(a => a.rank >= PICKER_MIN_RANK).length,
  }), [sorted]);
  const toggle = id => setIds(cur => cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id]);
  const makeHero = id => { setHero(id); setIds(cur => cur.filter(x => x !== id)); };
  // Back to no hero, which lets pickImages choose one at render time rather
  // than the deck carrying a stale choice. The image is not put back into the
  // supporting set: that is a separate decision, a click on the tile away.
  const clearHero = () => setHero(null);
  const suggest = () => { const pick = pickImages(sorted, imagesPerProject); setHero(pick.hero_file_id); setIds(pick.image_file_ids); };

  // Tagging is deliberately a separate gesture from choosing the image: the
  // tile body picks for this deck, the chips change what every deck starts
  // from. The star moves first and rolls back on failure, as rating does in
  // the press kit, because tagging a run of images has to stay responsive.
  const toggleTag = async (asset, tag) => {
    if (!canEdit || !asset.file_id) return;
    const key = `${asset.file_id}:${tag}`;
    if (tagBusy[key]) return;
    const current = tagEdits[asset.file_id] || asset.tags || [];
    const on = current.includes(tag);
    setTagEdits(cur => ({ ...cur, [asset.file_id]: on ? current.filter(t => t !== tag) : [...current, tag] }));
    setTagBusy(cur => ({ ...cur, [key]: true }));
    try {
      if (on) {
        // Every copy of the photograph, not only the one the deck uses:
        // projectAssets pools the tags across them, so a tag left on a sibling
        // copy would read as if this one were still tagged.
        await removeTagFromIds([asset.file_id, ...(asset.alias_ids || [])], tag);
      } else {
        await updateAssetTags([{
          fileId: asset.file_id, path: asset.path, name: asset.file_name,
          isVideo: false, thumbUrl: asset.thumb_url || null, projectCode: project.project_code || null,
        }], [tag]);
      }
      tagsWritten.current = true;
    } catch (e) {
      setTagEdits(cur => ({ ...cur, [asset.file_id]: current }));
      if (showToast) showToast(e.message, 'error');
    } finally {
      setTagBusy(cur => { const next = { ...cur }; delete next[key]; return next; });
    }
  };

  // Tag writes land in comms.asset_tags, which the tab fetched once. Refetch on
  // the way out rather than after each chip, so the grid stays still while you
  // work and the next Suggest still reads what you just tagged.
  const close = () => { if (tagsWritten.current && onTagsChanged) onTagsChanged(); onClose(); };
  const save = () => { if (tagsWritten.current && onTagsChanged) onTagsChanged(); onSave(hero, ids); };

  return (
    <Modal title={`Images for ${project.name || project.project_code}`} onClose={close} width={980}>
      {assets.length === 0
        ? <EmptyNote text="No ranked or tagged images for this project yet. Rank images in the project's press kit, then come back." />
        : (
          <>
            <p style={{ ...S.body, marginBottom: 12 }}>Click an image to add or remove it from the supporting set. Hero sets the lead image of the spread. The hashtag chips tag the photograph itself, so they steer what every future deck suggests for this project.{findingPreviews ? ' Fetching the missing previews…' : ''}</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
              {PICKER_FILTERS.map(f => (
                <FilterBtn key={f.key} label={`${f.label} (${counts[f.key]})`} active={filter === f.key} onClick={() => setFilter(f.key)} />
              ))}
            </div>
            {visible.length === 0
              ? <EmptyNote text={filter === 'tagged' ? 'Nothing tagged #pitch or #pitch-hero yet. Switch to 4★+ and tag from there.' : 'No image is ranked 4 stars or more. Switch to All, or rank the shoot in the press kit.'} />
              : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12 }}>
                  {visible.map(a => {
                    const isHero = hero === a.file_id;
                    const idx = ids.indexOf(a.file_id);
                    return (
                      <div key={a.file_id} style={{ position: 'relative', border: `2px solid ${isHero ? GOLD : idx >= 0 ? '#33473F' : 'transparent'}`, borderRadius: 3, padding: 3, background: '#F1EFE9' }}>
                        <div onClick={() => canEdit && !isHero && toggle(a.file_id)} style={{ cursor: canEdit && !isHero ? 'pointer' : 'default' }}>
                          <Thumb asset={a} size={150} />
                        </div>
                        {(isHero || idx >= 0) && (
                          <span style={{ position: 'absolute', top: 6, left: 6, fontFamily: MONO, fontSize: 10, background: isHero ? GOLD : '#33473F', color: '#fff', borderRadius: 2, padding: '1px 6px' }}>{isHero ? 'HERO' : idx + 1}</span>
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4, marginTop: 4, minHeight: 18 }}>
                          <Stars n={a.rank} />
                          <HeroChip isHero={isHero} canEdit={canEdit} fileName={a.file_name} onToggle={() => (isHero ? clearHero() : makeHero(a.file_id))} />
                        </div>
                        <div style={{ display: 'flex', gap: 4, marginTop: 3 }}>
                          <TagChip tag={PITCH_TAGS.hero} label="#pitch-hero" colour={GOLD} canEdit={canEdit} fileName={a.file_name}
                            on={a.tags.includes(PITCH_TAGS.hero)} busy={!!tagBusy[`${a.file_id}:${PITCH_TAGS.hero}`]} onToggle={t => toggleTag(a, t)} />
                          <TagChip tag={PITCH_TAGS.supporting} label="#pitch" colour="#33473F" canEdit={canEdit} fileName={a.file_name}
                            on={a.tags.includes(PITCH_TAGS.supporting)} busy={!!tagBusy[`${a.file_id}:${PITCH_TAGS.supporting}`]} onToggle={t => toggleTag(a, t)} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 18, flexWrap: 'wrap' }}>
              <span style={S.meta}>{hero ? 'Hero set' : 'No hero'} · {ids.length} supporting · showing {visible.length} of {sorted.length}</span>
              <span style={{ flex: 1 }} />
              {canEdit && <BtnGhost onClick={suggest}>Suggest again</BtnGhost>}
              {canEdit && <BtnPrimary onClick={save}>Save images</BtnPrimary>}
            </div>
          </>
        )}
    </Modal>
  );
}

// ── Chapter and project rows ─────────────────────────────────────────────────

function ProjectRow({ entry, project, assetById, chapters, chapterId, canEdit, allowConfidential, onPatch, onRemove, onMove, onImages }) {
  const hero = entry.hero_file_id ? assetById.get(entry.hero_file_id) : null;
  // A deck that allows confidential projects unlocks the checkbox whatever the
  // entry says, so a deck drafted before the setting existed is usable too.
  const lockedByNda = entry.confidential && !entry.confidential_override && !allowConfidential;
  const others = chapters.filter(c => c.id !== chapterId);
  return (
    <SortableRow id={entry.project_code} disabled={!canEdit} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid rgba(27,27,25,.08)', opacity: entry.include ? 1 : 0.6 }}>
      {({ attributes, listeners }) => (
        <>
          <span {...attributes} {...listeners} style={{ cursor: canEdit ? 'grab' : 'default', color: '#B0A9A0', display: 'inline-flex', touchAction: 'none' }} title="Drag to reorder"><GripVertical size={14} /></span>
          <input type="checkbox" checked={entry.include} disabled={!canEdit || lockedByNda} onChange={e => onPatch({ include: e.target.checked })} style={{ accentColor: GOLD }} title={lockedByNda ? 'Confidential: switch the override on first' : entry.include ? 'In the deck' : 'Left out'} />
          <Thumb asset={hero} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontFamily: MONO, fontSize: 11, letterSpacing: '0.1em', color: GOLD }}>{entry.project_code}</span>
              <span style={{ fontSize: 14, color: '#1B1B19' }}>{project ? project.name : 'Unknown project'}</span>
              {entry.confidential && (
                <Chip label={entry.confidential_override ? 'NDA · shown' : 'NDA · hidden'} cls={entry.confidential_override ? 'pipe' : 'new'}
                  onClick={canEdit ? () => onPatch(entry.confidential_override ? { confidential_override: false, include: false } : { confidential_override: true, include: true }) : undefined} />
              )}
              {!entry.hero_file_id && <span title="No lead image chosen" style={{ color: '#B8742A', display: 'inline-flex' }}><AlertTriangle size={13} /></span>}
            </div>
            {entry.reason && <div style={{ ...S.meta, fontSize: 11, marginTop: 2, whiteSpace: 'normal' }}>{entry.reason}</div>}
          </div>
          <span style={{ ...S.meta, whiteSpace: 'nowrap' }}>{entry.image_file_ids.length} img</span>
          <button onClick={onImages} title="Choose hero and supporting images" style={{ ...iconBtn, color: '#21302A' }}><ImagePlus size={15} /></button>
          {canEdit && others.length > 0 && (
            <select value="" onChange={e => { if (e.target.value) onMove(e.target.value); }} title="Move to another chapter" style={{ ...selectStyle, minWidth: 60, width: 60, padding: '3px 6px', fontSize: 11 }}>
              <option value="">→</option>
              {others.map(c => <option key={c.id} value={c.id}>{c.label || cap(c.category)}</option>)}
            </select>
          )}
          {canEdit && <button onClick={onRemove} title="Remove from deck" style={iconBtn}><Trash2 size={13} /></button>}
        </>
      )}
    </SortableRow>
  );
}

function ChapterCard({ chapter, chapters, projects, projectsByCode, usedCodes, assetById, canEdit, allowConfidential, onChange, onRemove, onPatchProject, onRemoveProject, onMoveProject, onAddProject, onImages }) {
  const sensors = useDragSensors();
  const ids = chapter.projects.map(p => p.project_code);
  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    onChange({ ...chapter, projects: arrayMove(chapter.projects, ids.indexOf(active.id), ids.indexOf(over.id)) });
  };
  const included = chapter.projects.filter(p => p.include).length;
  const cat = (chapter.category || '').toLowerCase();
  const remaining = projects.filter(p => p.project_code && !usedCodes.has(p.project_code));
  const inCategory = remaining.filter(p => (p.typology || '').toLowerCase() === cat);
  const others = remaining.filter(p => (p.typology || '').toLowerCase() !== cat);
  const optionLabel = p => `${p.project_code} · ${p.name}${p.confidential ? ' (NDA)' : ''}`;
  return (
    <div style={{ ...S.card, marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
        {canEdit
          ? <InlineInput value={chapter.label} onBlurSave={v => onChange({ ...chapter, label: v })} placeholder="Chapter title…" style={{ flex: 1, minWidth: 180, fontSize: 16, fontFamily: "'Spectral', Georgia, serif", border: 'none', background: 'transparent', padding: '2px 0' }} />
          : <h3 style={{ ...S.h3, flex: 1 }}>{chapter.label || cap(chapter.category)}</h3>}
        {chapter.category && <Chip label={chapter.category} cls="line" />}
        <span style={S.meta}>{included} of {chapter.projects.length} in deck</span>
        {canEdit && <button onClick={onRemove} title="Remove chapter" style={iconBtn}><Trash2 size={13} /></button>}
      </div>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          {chapter.projects.map(p => (
            <ProjectRow key={p.project_code} entry={p} project={projectsByCode.get(p.project_code)} assetById={assetById} chapters={chapters} chapterId={chapter.id} canEdit={canEdit} allowConfidential={allowConfidential}
              onPatch={fields => onPatchProject(p.project_code, fields)} onRemove={() => onRemoveProject(p.project_code)} onMove={target => onMoveProject(p.project_code, target)} onImages={() => onImages(p.project_code)} />
          ))}
        </SortableContext>
      </DndContext>
      {chapter.projects.length === 0 && <EmptyNote text="No projects in this chapter." />}
      {canEdit && remaining.length > 0 && (
        <AddLine>
          <select value="" onChange={e => { if (e.target.value) onAddProject(e.target.value); }} style={selectStyle} title="Add a project to this chapter">
            <option value="">+ Add a project…</option>
            {inCategory.length > 0 && <optgroup label={chapter.category ? `In ${chapter.category}` : 'Projects'}>{inCategory.map(p => <option key={p.project_code} value={p.project_code}>{optionLabel(p)}</option>)}</optgroup>}
            {others.length > 0 && <optgroup label="Other categories">{others.map(p => <option key={p.project_code} value={p.project_code}>{optionLabel(p)}</option>)}</optgroup>}
          </select>
        </AddLine>
      )}
    </div>
  );
}

// ── Builder ──────────────────────────────────────────────────────────────────

export function PitchDeckBuilder({ deck, data, canEdit, refresh, showToast, onBack }) {
  const types = data.pitch_types || [];
  const type = types.find(t => t.comms_id === deck.pitch_type_id) || null;
  const projects = useMemo(() => data.projects || [], [data.projects]);
  const projectsByCode = useMemo(() => new Map(projects.map(p => [p.project_code, p])), [projects]);
  const categories = useMemo(() => categoriesFrom(data.project_categories, projects), [data.project_categories, projects]);
  const ranks = useMemo(() => data.asset_ranks || [], [data.asset_ranks]);
  const tags = useMemo(() => data.asset_tags || [], [data.asset_tags]);
  const assetById = useMemo(() => {
    const m = new Map();
    [...ranks, ...tags].forEach(r => {
      if (!r.dropbox_file_id) return;
      const cur = m.get(r.dropbox_file_id) || { file_id: r.dropbox_file_id, file_name: r.file_name || '', thumb_url: null };
      if (r.thumb_url && !cur.thumb_url) cur.thumb_url = r.thumb_url;
      m.set(r.dropbox_file_id, cur);
    });
    return m;
  }, [ranks, tags]);
  const rate = (data.fx_rates && data.fx_rates[0] && Number(data.fx_rates[0].avg_rate)) || DEFAULT_EUR_INR;
  const inquiry = useMemo(() => (data.inquiries || []).find(i => i.id === deck.inquiry_id) || null, [data.inquiries, deck.inquiry_id]);
  const client = useMemo(() => inquiry ? (data.clients || []).find(c => Number(c.id) === Number(inquiry.client_id)) || null : null, [data.clients, inquiry]);
  const profile = useMemo(() => inquiry ? inquiryProfile(inquiry, client, rate) : null, [inquiry, client, rate]);

  const [chapters, setChapters] = useState(() => readDeckChapters(deck.chapters));
  const [sections, setSections] = useState(() => readDeckSections(deck.sections));
  // Whether NDA projects may go in this deck. Set when the deck is drafted and
  // changeable here; it used to be a checkbox on the New deck form that nothing
  // remembered, so a hand added or re-suggested project was always refused.
  const allowConfidential = !!deck.allow_confidential;
  const [picker, setPicker] = useState(null);
  const [prep, setPrep] = useState(null);            // progress text while images are prepared
  const [rendering, setRendering] = useState(false);
  const [renderStatus, setRenderStatus] = useState(null);  // what the render is doing, on screen
  const [renderError, setRenderError] = useState(null);    // stays until the next attempt
  const [lastRender, setLastRender] = useState(null);      // { url, name, bytes } of the newest PDF
  const [portraitUrls, setPortraitUrls] = useState({});    // user:<id> to a signed profile photo
  const [versions, setVersions] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [previewWidth, setPreviewWidth] = useState(900);
  const previewRef = useRef(null);
  useEffect(() => { setChapters(readDeckChapters(deck.chapters)); setSections(readDeckSections(deck.sections)); }, [deck.comms_id, deck.updated_at]); // eslint-disable-line react-hooks/exhaustive-deps

  const persist = async fields => {
    try { await commsUpdate('pitch_decks', 'comms_id', deck.comms_id, { ...fields, updated_at: now() }); refresh(); }
    catch (e) { showToast(e.message, 'error'); }
  };
  const saveChapters = next => { setChapters(next); persist({ chapters: next }); };
  const saveSections = next => { setSections(next); persist({ sections: next }); };

  const usedCodes = useMemo(() => new Set(chapters.flatMap(c => c.projects.map(p => p.project_code))), [chapters]);
  const imagesPerProject = type ? Number(type.images_per_project) || 6 : 6;

  const evaluateEntry = code => {
    const project = projectsByCode.get(code);
    if (!project) return null;
    const assets = projectAssets(code, ranks, tags);
    const scored = scoreProject(project, { category: profile ? profile.category : '', awards: data.awards || [], assets, allowConfidential });
    const images = pickImages(assets, imagesPerProject);
    return {
      project_code: code, include: scored.blockers.length === 0, confidential: !!project.confidential, confidential_override: allowConfidential && !!project.confidential,
      hero_file_id: images.hero_file_id, image_file_ids: images.image_file_ids, headline_override: null, text_override: null,
      reason: `added by hand; ${[...scored.reasons, ...images.reasons].join(', ')}${scored.blockers.length ? ` (${scored.blockers.join(', ')})` : ''}`, blockers: scored.blockers,
    };
  };

  const patchChapter = next => saveChapters(chapters.map(c => c.id === next.id ? next : c));
  const removeChapter = id => {
    const ch = chapters.find(c => c.id === id);
    if (ch && ch.projects.length && !window.confirm(`Remove the chapter "${ch.label || ch.category}" and its ${ch.projects.length} projects from this deck?`)) return;
    saveChapters(chapters.filter(c => c.id !== id));
  };
  const addChapter = category => saveChapters([...chapters, { id: newId('ch'), category, label: (categories.find(c => c.key === category) || {}).label || cap(category), projects: [] }]);
  const patchProject = (chapterId, code, fields) => saveChapters(chapters.map(c => c.id !== chapterId ? c : { ...c, projects: c.projects.map(p => p.project_code === code ? { ...p, ...fields } : p) }));
  const removeProject = (chapterId, code) => saveChapters(chapters.map(c => c.id !== chapterId ? c : { ...c, projects: c.projects.filter(p => p.project_code !== code) }));
  const moveProject = (chapterId, code, targetId) => {
    const entry = chapters.find(c => c.id === chapterId)?.projects.find(p => p.project_code === code);
    if (!entry) return;
    saveChapters(chapters.map(c => c.id === chapterId ? { ...c, projects: c.projects.filter(p => p.project_code !== code) } : c.id === targetId ? { ...c, projects: [...c.projects, entry] } : c));
  };
  const addProject = (chapterId, code) => {
    const entry = evaluateEntry(code);
    if (!entry) return;
    saveChapters(chapters.map(c => c.id === chapterId ? { ...c, projects: [...c.projects, entry] } : c));
  };
  const saveImages = (chapterId, code, hero, ids) => { patchProject(chapterId, code, { hero_file_id: hero, image_file_ids: ids }); setPicker(null); };

  // Turning it on only unlocks the rows; which NDA projects go in stays a
  // decision per project. Turning it off is the safe direction, so it takes
  // them back out rather than leaving a confidential project in a deck that no
  // longer allows one.
  const toggleConfidential = on => {
    if (on) { persist({ allow_confidential: true }); return; }
    const pulled = chapters.flatMap(c => c.projects).filter(p => p.confidential && (p.include || p.confidential_override)).length;
    const next = chapters.map(c => ({ ...c, projects: c.projects.map(p => p.confidential ? { ...p, confidential_override: false, include: false } : p) }));
    setChapters(next);
    persist({ allow_confidential: false, chapters: next });
    if (pulled) showToast(`${pulled} confidential project${pulled === 1 ? '' : 's'} taken out of the deck`);
  };

  const resuggest = () => {
    if (!type) return;
    if (!window.confirm('Replace the chapters, projects, images and sections of this deck with a fresh suggestion from the pitch type? Title, intro and quality stay.')) return;
    // A deck drafted before the column existed records its answer only on the
    // entries, so an override that is already there still counts as a yes.
    const allow = allowConfidential || chapters.some(c => c.projects.some(p => p.confidential_override));
    const draft = buildDraftDeck({ inquiry, client, profile, pitchType: type, projects, ranks, tags, awards: data.awards || [], publications: data.publications || [], themes: data.themes || [], themeAssets: data.theme_assets || [], allowConfidential: allow, eurInrRate: rate });
    setChapters(draft.chapters); setSections(draft.sections);
    persist({ chapters: draft.chapters, sections: draft.sections, allow_confidential: allow });
    showToast(draft.reasons.length ? draft.reasons.join('. ') : 'Suggestion refreshed');
  };
  const changeType = async id => {
    await persist({ pitch_type_id: id || null });
  };
  const deleteDeck = async () => {
    if (!window.confirm(`Delete the deck "${deck.title}"? Rendered PDFs are not removed from storage.`)) return;
    try { await commsDelete('pitch_decks', 'comms_id', deck.comms_id); showToast('Deck deleted'); onBack(); refresh(); }
    catch (e) { showToast(e.message, 'error'); }
  };


  // ── Images, render, versions, preview ──────────────────────────────────────

  // Every Dropbox file the render needs, grouped by the folder it lives in,
  // because the thumbnail function works per folder.
  // Every Dropbox file the deck needs, and which of them are lead images. No
  // folder paths: those are resolved by file id now, see prepareImages.
  const imageNeeds = () => {
    const ids = new Set();
    const heroes = new Set();
    chapters.forEach(c => c.projects.filter(p => p.include).forEach(p => { if (p.hero_file_id) { ids.add(p.hero_file_id); heroes.add(p.hero_file_id); } p.image_file_ids.forEach(id => ids.add(id)); }));
    sections.filter(s => s.enabled && s.key === 'theme' && s.settings.theme_id).forEach(s => (data.theme_assets || []).forEach(t => { if (t.theme_id === s.settings.theme_id && t.dropbox_file_id) ids.add(t.dropbox_file_id); }));
    return { ids, heroes };
  };

  // Derive the presentation tier for every needed file, a few at a time.
  // Returns object paths in the pitch-images bucket keyed on file id; a file
  // that fails keeps its 2048 px fallback and is counted for the toast.
  const derivePresentationImages = async (needs, onProgress) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error('Session expired, sign in again');
    const hashes = needs.map(n => n.contentHash);
    const { data: cachedRows, error: cacheErr } = await supabase.schema('comms').from('pitch_images')
      .select('content_hash, object_path').eq('width', PRESENTATION_WIDTH).in('content_hash', hashes);
    if (cacheErr) throw cacheErr;
    const cached = new Map((cachedRows || []).map(r => [r.content_hash, r.object_path]));
    const paths = new Map();
    const pending = [];
    needs.forEach(n => { if (cached.has(n.contentHash)) paths.set(n.id, cached.get(n.contentHash)); else pending.push(n); });
    let done = 0, failed = 0;
    const worker = async () => {
      while (pending.length) {
        const n = pending.shift();
        try {
          const res = await fetch('/api/pitch-image', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
            body: JSON.stringify({ path: n.path, contentHash: n.contentHash, fileName: n.fileName }),
          });
          const json = await res.json().catch(() => ({}));
          if (!res.ok || !json.path) throw new Error(json.error || `HTTP ${res.status}`);
          paths.set(n.id, json.path);
        } catch (e) {
          failed += 1;
          console.warn('[pitch] presentation image failed', n.fileName, e.message);
        }
        done += 1;
        onProgress(done, pending.length + done);
      }
    };
    await Promise.all(Array.from({ length: Math.min(PRESENTATION_PARALLEL, pending.length) }, worker));
    return { paths, failed };
  };

  // Portraits live in Supabase storage, so they need no Dropbox round trip:
  // they are signed as the builder opens and are in the preview straight away.
  // Prepare images later replaces them with the Dropbox original.
  useEffect(() => {
    let cancelled = false;
    const wantTeam = sections.some(x => x.enabled && x.key === 'team');
    const wantLeadership = sections.some(x => x.enabled && x.key === 'founders');
    if (!wantTeam && !wantLeadership) return undefined;
    const shown = new Set((data.users || []).filter(u => wantTeam || isLeadershipRole(u.job_role)).map(u => Number(u.id)));
    const rows = (data.user_profiles || []).filter(pr => shown.has(Number(pr.user_id)) && pr.profile_photo_rect && !String(pr.profile_photo_rect).startsWith('data:'));
    if (!rows.length) return undefined;
    (async () => {
      const { data: signed, error } = await supabase.storage.from('profile-photos').createSignedUrls(rows.map(pr => pr.profile_photo_rect), SIGNED_URL_SECONDS);
      if (cancelled || error) return;
      const next = {};
      (signed || []).forEach((sg, i) => { if (sg.signedUrl) next[`user:${rows[i].user_id}`] = sg.signedUrl; });
      setPortraitUrls(next);
    })();
    return () => { cancelled = true; };
  }, [data.users, data.user_profiles, sections]);

  // The best portrait file per member from Dropbox: the newest uncropped
  // upload in Originals, else the newest file there, else the portrait in the
  // member folder. Returns thumbnail URLs keyed on user id.
  const resolvePortraits = async (members, onProgress) => {
    const pickFile = (entries, folder) => {
      const images = (entries || []).filter(e => !e.is_folder && e.is_image && e.file_id);
      if (!images.length) return null;
      const newest = list => list.slice().sort((a, b) => String(b.client_modified).localeCompare(String(a.client_modified)))[0];
      const f = newest(images.filter(e => /—\s*Source/.test(e.name))) || newest(images);
      return { ...f, path: `${folder}/${f.name}` };
    };
    const chosen = new Map();
    let done = 0, failed = 0;
    const queue = members.slice();
    const worker = async () => {
      while (queue.length) {
        const m = queue.shift();
        try {
          const originals = await withTimeout(callEdgeFunction('list-press-folder-contents', { folderPath: `${m.folder}/${ORIGINALS_FOLDER}`, thumbs: true, thumbSize: PORTRAIT_THUMB_SIZE, previewLinks: false }), FOLDER_TIMEOUT_MS, 'The portrait folder listing');
          let file = pickFile(originals.entries, `${m.folder}/${ORIGINALS_FOLDER}`);
          let gone = originals.folder_missing === true;
          if (!file) {
            const own = await withTimeout(callEdgeFunction('list-press-folder-contents', { folderPath: m.folder, thumbs: true, thumbSize: PORTRAIT_THUMB_SIZE, previewLinks: false }), FOLDER_TIMEOUT_MS, 'The portrait folder listing');
            file = pickFile(own.entries, m.folder);
            gone = gone && own.folder_missing === true;
          }
          // A folder that is not there any more is a different problem from a
          // folder with no portrait in it, and only one of them is fixed by
          // uploading a photograph.
          if (gone) console.warn('[pitch] portrait folder is missing in Dropbox', m.folder);
          if (file) chosen.set(m.id, file); else failed += 1;
        } catch (e) {
          failed += 1;
          console.warn('[pitch] portrait folder failed', m.folder, e.message);
        }
        done += 1;
        onProgress(done, members.length);
      }
    };
    await Promise.all(Array.from({ length: Math.min(PRESENTATION_PARALLEL, queue.length) }, worker));

    // No presentation derivation here whatever the deck's tier: a portrait is
    // shown three or four to a half page, so a 3840 px original would be
    // downscaled by a factor of eighteen on the page and cost the render more
    // than every project photograph put together.
    const found = new Map();
    chosen.forEach((f, id) => { if (f.thumb_url) found.set(id, f.thumb_url); });
    return { found, failed };
  };

  const prepareImages = async () => {
    const { ids, heroes } = imageNeeds();
    const urls = { ...(deck.image_urls && typeof deck.image_urls === 'object' ? deck.image_urls : {}) };
    const presentation = deck.quality === 'presentation';
    const notes = [];
    try {
      // Resolved by Dropbox file id, never by the folder path stored beside
      // the star. An id survives a rename or a move; a path does not, and when
      // one press kit's `Landscape` folder became `JPEG-highres` two projects
      // came back from this step with no pictures at all and nothing said so.
      // Two rounds: the framed and gallery tiers for every picture, then the
      // page filling tier for the lead images, which are the only ones bled.
      const needs = [];
      const wanted = Array.from(ids);
      const heroIds = wanted.filter(id => heroes.has(id));
      const missingIds = new Set(wanted);
      const rounds = [[FRAME_THUMB_SIZES, wanted], [HERO_THUMB_SIZES, heroIds]];
      const total = wanted.length + heroIds.length;
      let done = 0;
      for (const [sizes, idList] of rounds) {
        for (let i = 0; i < idList.length; i += RESOLVE_BATCH) {
          const batch = idList.slice(i, i + RESOLVE_BATCH);
          setPrep(`Images: ${Math.min(done + batch.length, total)} of ${total}`);
          // `thumbSize` alongside `thumbSizes` so a listing function that does
          // not know about several sizes yet still answers with the first one
          // rather than falling back to its own 480 px default.
          const res = await withTimeout(
            callEdgeFunction('list-press-folder-contents', { fileIds: batch, thumbs: true, thumbSizes: sizes, thumbSize: sizes[0] }),
            FOLDER_TIMEOUT_MS, 'The image lookup',
          );
          (res.entries || []).forEach(e => {
            if (!e.file_id || !ids.has(e.file_id)) return;
            const tiers = tiersFrom(e, sizes);
            if (!Object.keys(tiers).length) return;
            urls[e.file_id] = mergeTiers(urls[e.file_id], tiers);
            missingIds.delete(e.file_id);
            // The presentation tier fetches the original, and Dropbox takes an
            // id wherever it takes a path.
            if (presentation && heroes.has(e.file_id) && e.content_hash && e.is_image) {
              needs.push({ id: e.file_id, contentHash: e.content_hash, fileName: e.name, path: e.file_id });
            }
          });
          done += batch.length;
        }
      }
      if (presentation && needs.length) {
        setPrep(`Presentation images: 0 of ${needs.length}`);
        const { paths, failed } = await derivePresentationImages(needs, (done, total) => setPrep(`Presentation images: ${done} of ${total}`));
        if (paths.size) {
          const entries = Array.from(paths.entries());
          const { data: signed, error } = await supabase.storage.from('pitch-images').createSignedUrls(entries.map(([, p]) => p), SIGNED_URL_SECONDS);
          if (error) throw error;
          (signed || []).forEach((sg, i) => { if (sg.signedUrl) urls[entries[i][0]] = mergeTiers(urls[entries[i][0]], { l: sg.signedUrl }); });
        }
        if (failed) notes.push(`${failed} kept the 2048 px version`);
      }
      const wantTeam = sections.some(s => s.enabled && s.key === 'team');
      const wantLeadership = sections.some(s => s.enabled && s.key === 'founders');
      if (wantTeam || wantLeadership) {
        // The app's crop first, as the fallback for everyone with a photo.
        setPrep('Portraits');
        const withPhoto = (data.user_profiles || []).filter(pr => pr.profile_photo_rect && !String(pr.profile_photo_rect).startsWith('data:'));
        if (withPhoto.length) {
          const { data: signed, error } = await supabase.storage.from('profile-photos').createSignedUrls(withPhoto.map(pr => pr.profile_photo_rect), SIGNED_URL_SECONDS);
          if (error) throw error;
          (signed || []).forEach((sg, i) => { if (sg.signedUrl) urls[`user:${withPhoto[i].user_id}`] = sg.signedUrl; });
        }
        // Then the Dropbox original for every member the deck shows.
        const shown = (data.users || []).filter(u => wantTeam || isLeadershipRole(u.job_role));
        const folderByUser = new Map((data.team_portrait_folders || []).filter(f => f.bucket === 'Active' && f.folder_name).map(f => [Number(f.user_id), f]));
        const members = shown.filter(u => folderByUser.has(Number(u.id))).map(u => ({ id: u.id, folder: `${PORTRAITS_BASE}/Active/${folderByUser.get(Number(u.id)).folder_name}` }));
        if (members.length) {
          const { found, failed } = await resolvePortraits(members, (done, total) => setPrep(`Portraits: ${done} of ${total}`));
          found.forEach((url, id) => { urls[`user:${id}`] = url; });
          if (failed) notes.push(`${failed} portrait${failed === 1 ? '' : 's'} kept the app photo`);
        }
      }
      const missing = missingIds.size;
      if (missing) notes.push(`${missing} could not be found in Dropbox`);
      await persist({ image_urls: urls, images_prepared_at: now() });
      showToast(notes.length ? `Images prepared, ${notes.join(', ')}` : `Images prepared, ${Object.keys(urls).length} in the deck`);
      return urls;
    } catch (e) { showToast(e.message, 'error'); setRenderError(e.message); return null; }
    finally { setPrep(null); }
  };

  // Claude rewrites the opening for this client, from the studio's own words
  // plus what the inquiry says. Offered, not applied: the reviewer sees it in
  // the field and can undo it with Use the base intro. The studio texts are
  // never touched. See docs/pitch-deck-generator-plan.md, section 6.
  const [rewriting, setRewriting] = useState(false);
  const rewriteIntro = async () => {
    if (rewriting) return;
    if (!inquiry && !window.confirm('This deck is not linked to an inquiry, so the rewrite has only the studio texts to work from. Rewrite anyway?')) return;
    setRewriting(true);
    try {
      const res = await fetch('/api/comms-draft', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'pitch-intro',
          context: {
            baseIntro: deck.intro_text || (type && type.intro_template) || '',
            studioBio: (data.practice && data.practice.studio_bio) || '',
            mission: (data.practice && data.practice.mission) || '',
            clientName: deck.client_name || (inquiry && (inquiry.client_org_name_override || inquiry.client_name_override)) || (client && (client.display_name || client.name)) || '',
            clientType: client ? client.client_type : '',
            projectType: inquiry ? inquiry.project_type : '',
            city: inquiry ? inquiry.location_city : '',
            briefNotes: inquiry ? inquiry.brief_notes : '',
          },
        }),
      });
      const text = await res.text();
      let json = {};
      try { json = JSON.parse(text); } catch { /* not JSON */ }
      if (!res.ok) throw new Error(json.error || `The rewrite failed (HTTP ${res.status}). ${text.slice(0, 200).replace(/<[^>]*>/g, ' ').trim()}`);
      const draft = String(json.draft || '').trim();
      if (!draft) throw new Error('The rewrite came back empty');
      await persist({ intro_text: draft, intro_generated_at: now() });
      showToast('Introduction rewritten, edit it or put the base intro back');
    } catch (e) { showToast(e.message, 'error'); }
    finally { setRewriting(false); }
  };

  const renderModel = urls => assemblePitchRender({
    deck: { ...deck, chapters, sections, image_urls: { ...portraitUrls, ...(urls || deck.image_urls || {}) } },
    type, data,
  });
  const imagesStale = !deck.images_prepared_at || new Date(deck.images_prepared_at) < new Date(deck.updated_at) || Date.now() - new Date(deck.images_prepared_at).getTime() > SIGNED_URL_STALE_MS;

  const renderPdf = async () => {
    setRendering(true);
    setRenderError(null);
    try {
      setRenderStatus('Preparing images…');
      const urls = imagesStale ? await prepareImages() : null;
      if (imagesStale && !urls) return;
      const model = renderModel(urls);
      if (model.missingHero.length) showToast(`No lead image for ${model.missingHero.join(', ')}; those spreads open on a plain page`);
      const { html, pageCount } = buildPitchHtml(model);
      setRenderStatus(`Rendering ${pageCount} spread${pageCount === 1 ? '' : 's'}…`);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Session expired, sign in again');
      const filename = `BRIO_Pitch_${String(deck.title || 'deck').replace(/[^A-Za-z0-9]+/g, '_').replace(/^_+|_+$/g, '')}.pdf`;
      const res = await withTimeout(fetch('/api/pitch-pdf', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ deckId: deck.comms_id, html, filename, width: model.format.width, height: model.format.height }),
      }), RENDER_TIMEOUT_MS, 'The render');
      // A gateway timeout answers with HTML, so say what came back rather than
      // letting the JSON parse swallow it.
      const text = await res.text();
      let json = {};
      try { json = JSON.parse(text); } catch { /* not JSON */ }
      if (!res.ok) throw new Error(json.error || `The render failed (HTTP ${res.status}). ${text.slice(0, 200).replace(/<[^>]*>/g, ' ').trim()}`);
      if (!json.url) throw new Error('The render came back without a file');
      setLastRender({ url: json.url, name: filename, bytes: json.bytes });
      refresh(); loadVersions();
      showToast(`Rendered, ${fmtMb(json.bytes)}${json.bytes < ATTACHMENT_MAX_BYTES ? ', small enough to attach' : ', send as a link'}`);
    } catch (e) {
      setRenderError(e.message);
      showToast(e.message, 'error');
    } finally { setRendering(false); setRenderStatus(null); }
  };

  // Safari refuses a window opened after an await, so a rendered PDF is never
  // opened for the reviewer: it is handed over as a link and a download button
  // they click themselves.
  const downloadPdf = async (url, name) => {
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Could not fetch the file (HTTP ${res.status})`);
      const blob = await res.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href; a.download = name || 'BRIO_Pitch.pdf';
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(href), 10000);
    } catch (e) { showToast(e.message, 'error'); }
  };

  // Sign every version as the list loads, so Open is a plain link the reviewer
  // clicks. Signing on click and then opening a window is what Safari blocks.
  const loadVersions = useCallback(async () => {
    const { data: files, error } = await supabase.storage.from('pitch-decks').list(deck.comms_id, { limit: 50, sortBy: { column: 'created_at', order: 'desc' } });
    if (error) return;
    const pdfs = (files || []).filter(f => /\.pdf$/i.test(f.name));
    if (!pdfs.length) { setVersions([]); return; }
    const { data: signed } = await supabase.storage.from('pitch-decks').createSignedUrls(pdfs.map(f => `${deck.comms_id}/${f.name}`), 3600);
    setVersions(pdfs.map((f, i) => ({ ...f, url: (signed && signed[i] && signed[i].signedUrl) || null })));
  }, [deck.comms_id]);
  useEffect(() => { loadVersions(); }, [loadVersions, deck.rendered_at]);

  useEffect(() => {
    if (!showPreview || !previewRef.current) return undefined;
    const el = previewRef.current;
    const measure = () => setPreviewWidth(Math.max(320, el.clientWidth - 20));
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [showPreview]);

  const preview = useMemo(() => {
    if (!showPreview) return null;
    const model = renderModel();
    const scale = Math.min(1, previewWidth / model.format.width);
    const { html, pageCount } = buildPitchHtml(model, { scale });
    return { html, pageCount, lowRes: model.lowResCount, height: Math.ceil(pageCount * (model.format.height + 24) * scale) + 8 };
  }, [showPreview, previewWidth, chapters, sections, deck, data, type, portraitUrls]); // eslint-disable-line react-hooks/exhaustive-deps

  const included = chapters.flatMap(c => c.projects.filter(p => p.include));
  const excluded = chapters.flatMap(c => c.projects.filter(p => !p.include).map(p => ({ ...p, chapter: c.label || cap(c.category) })));
  const pickerEntry = picker ? chapters.find(c => c.id === picker.chapterId)?.projects.find(p => p.project_code === picker.code) : null;
  const feeBandLabel = profile && profile.feeBand ? (FEE_BANDS.find(b => b.key === profile.feeBand) || {}).label : null;

  return (
    <div>
      <BackBtn onClick={onBack} label="All decks" />
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap', marginBottom: 20 }}>
        <div style={{ flex: 1, minWidth: 280 }}>
          <div style={{ ...S.eyebrow, marginBottom: 6 }}>{[type ? type.label : 'No pitch type', deck.client_name].filter(Boolean).join(' · ')}</div>
          {canEdit
            ? <InlineInput value={deck.title} onBlurSave={v => { if (v.trim()) persist({ title: v.trim() }); }} placeholder="Deck title…" style={{ ...S.h1, border: 'none', background: 'transparent', padding: '2px 0', width: '100%' }} />
            : <h1 style={S.h1}>{deck.title}</h1>}
          {profile
            ? <p style={{ ...S.meta, marginTop: 6 }}>Inquiry: {[profile.projectType, feeBandLabel, profile.clientType === 'family' ? 'private client' : profile.clientType === 'org' ? 'organisation' : null, profile.city].filter(Boolean).join(' · ')}</p>
            : deck.inquiry_id
              ? <p style={{ ...S.meta, marginTop: 6 }}>Made from an inquiry that is no longer active.</p>
              : <p style={{ ...S.meta, marginTop: 6 }}>Not tied to an inquiry.</p>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <Chip label={deck.status || 'draft'} cls={statusClass(deck.status === 'sent' ? 'done' : deck.status)} onClick={canEdit ? () => persist({ status: deck.status === 'sent' ? 'draft' : 'sent' }) : undefined} />
          <select value={deck.pitch_type_id || ''} disabled={!canEdit} onChange={e => changeType(e.target.value)} style={selectStyle} title="Pitch type">
            <option value="">No pitch type</option>
            {types.map(t => <option key={t.comms_id} value={t.comms_id}>{t.label}</option>)}
          </select>
          <select value={deck.quality || 'email'} disabled={!canEdit} onChange={e => persist({ quality: e.target.value })} style={selectStyle} title="Image quality tier">
            {QUALITY_TIERS.map(q => <option key={q.key} value={q.key}>{q.label}</option>)}
          </select>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#1B1B19', cursor: canEdit ? 'pointer' : 'default', whiteSpace: 'nowrap' }} title="Confidential (NDA) projects may be put in this deck">
            <input type="checkbox" checked={allowConfidential} disabled={!canEdit} onChange={e => toggleConfidential(e.target.checked)} style={{ accentColor: GOLD }} />
            Allow NDA projects
          </label>
          {canEdit && type && <BtnGhost onClick={resuggest}><RefreshCw size={12} style={{ marginRight: 6, verticalAlign: -2 }} />Suggest again</BtnGhost>}
          {canEdit && <BtnGhost onClick={prep || rendering ? undefined : prepareImages} disabled={!!(prep || rendering)}>{prep || 'Prepare images'}</BtnGhost>}
          <BtnGhost onClick={() => setShowPreview(v => !v)}>{showPreview ? 'Hide preview' : 'Preview'}</BtnGhost>
          {canEdit && <BtnPrimary onClick={rendering || prep ? undefined : renderPdf} disabled={!!(rendering || prep)}>{renderStatus || (rendering ? 'Rendering…' : 'Render PDF')}</BtnPrimary>}
          {canEdit && <button onClick={deleteDeck} title="Delete deck" style={iconBtn}><Trash2 size={14} /></button>}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 360px', gap: 20, alignItems: 'start' }}>
        <div>
          <BlockTitle left={`Chapters and projects`} right={`${included.length} project${included.length === 1 ? '' : 's'} in the deck`} />
          {chapters.map(ch => (
            <ChapterCard key={ch.id} chapter={ch} chapters={chapters} projects={projects} projectsByCode={projectsByCode} usedCodes={usedCodes} assetById={assetById} canEdit={canEdit} allowConfidential={allowConfidential}
              onChange={patchChapter} onRemove={() => removeChapter(ch.id)}
              onPatchProject={(code, fields) => patchProject(ch.id, code, fields)} onRemoveProject={code => removeProject(ch.id, code)} onMoveProject={(code, target) => moveProject(ch.id, code, target)}
              onAddProject={code => addProject(ch.id, code)} onImages={code => setPicker({ chapterId: ch.id, code })} />
          ))}
          {chapters.length === 0 && <EmptyNote text="No chapters. Add one below or use Suggest again." />}
          {canEdit && (
            <AddLine>
              <select value="" onChange={e => { if (e.target.value) addChapter(e.target.value); }} style={selectStyle}>
                <option value="">Add a chapter…</option>
                {categories.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </AddLine>
          )}
        </div>

        {/* minmax(0, 1fr): an auto column would grow to the widest row and push
            the whole column past the page edge. */}
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 24, minWidth: 0 }}>
          <div>
            <BlockTitle left="Sections, in order" />
            <SectionsEditor sections={sections} themes={data.themes || []} canEdit={canEdit} onChange={saveSections} />
          </div>
          <div>
            <BlockTitle left="Introduction" />
            <NoteArea value={deck.intro_text || ''} onChange={v => persist({ intro_text: v })} placeholder={type && type.intro_template ? type.intro_template : 'Dear …'} minHeight={140} saveOnBlur />
            <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              {canEdit && type && type.intro_template && (deck.intro_text || '') !== type.intro_template && <BtnGhost onClick={() => persist({ intro_text: type.intro_template })}>Use the base intro</BtnGhost>}
              {canEdit && <BtnGhost onClick={rewriteIntro} disabled={!!rewriting}>{rewriting ? 'Rewriting…' : 'Rewrite for this client'}</BtnGhost>}
            </div>
            {deck.intro_generated_at && <p style={{ ...S.meta, marginTop: 6 }}>Rewritten {new Date(deck.intro_generated_at).toLocaleString('en-GB')}</p>}
          </div>
          <div>
            <BlockTitle left="Rendered versions" />
            {renderError && (
              <div style={{ border: '1px solid #B8742A', borderRadius: 3, background: 'rgba(184,116,42,.07)', padding: '10px 12px', marginBottom: 12 }}>
                <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#B8742A', marginBottom: 4 }}>The last render did not finish</div>
                <div style={{ fontSize: 13, lineHeight: 1.5 }}>{renderError}</div>
              </div>
            )}
            {lastRender && (
              <div style={{ border: '1px solid rgba(27,27,25,.14)', borderRadius: 3, padding: '10px 12px', marginBottom: 12 }}>
                <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#6E6C66', marginBottom: 6 }}>Last render · {fmtMb(lastRender.bytes)}</div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <a href={lastRender.url} target="_blank" rel="noopener noreferrer" style={{ fontFamily: MONO, fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#21302A', border: '1px solid #33473F', borderRadius: 3, padding: '7px 11px', textDecoration: 'none' }}>Open</a>
                  <button onClick={() => downloadPdf(lastRender.url, lastRender.name)} style={{ fontFamily: MONO, fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase', background: 'none', color: '#21302A', border: '1px solid #33473F', borderRadius: 3, padding: '7px 11px', cursor: 'pointer' }}>Download</button>
                </div>
              </div>
            )}
            {versions.length === 0
              ? <p style={S.body}>Nothing rendered yet. Render PDF prepares the images first when the selection changed since the last time.</p>
              : versions.map(v => (
                <div key={v.name} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: '1px solid rgba(27,27,25,.08)' }}>
                  <span style={{ flex: 1, fontSize: 13, minWidth: 0 }}>{v.created_at ? new Date(v.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : v.name}</span>
                  <span style={{ ...S.meta, fontFamily: MONO }}>{v.metadata && v.metadata.size != null ? fmtMb(v.metadata.size) : ''}</span>
                  {v.metadata && v.metadata.size != null && <Chip label={v.metadata.size < ATTACHMENT_MAX_BYTES ? 'attach' : 'link'} cls={v.metadata.size < ATTACHMENT_MAX_BYTES ? 'pub' : 'pipe'} />}
                  {v.url && <a href={v.url} target="_blank" rel="noopener noreferrer" title="Open this version" style={{ ...iconBtn, color: '#21302A' }}><FileDown size={14} /></a>}
                  {v.url && <button onClick={() => downloadPdf(v.url, v.name)} title="Download this version" style={{ ...iconBtn, color: '#21302A', fontFamily: MONO, fontSize: 10 }}>↓</button>}
                </div>
              ))}
            {deck.images_prepared_at && (
              <p style={{ ...S.meta, marginTop: 8 }}>Images prepared {new Date(deck.images_prepared_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}{imagesStale ? (Date.now() - new Date(deck.images_prepared_at).getTime() > SIGNED_URL_STALE_MS ? ', links since expired' : ', before the last change') : ''}.</p>
            )}
          </div>
          <div>
            <BlockTitle left="Left out by the engine" />
            {excluded.length === 0
              ? <p style={S.body}>Every suggested project is in the deck.</p>
              : excluded.map(p => (
                <div key={p.project_code} style={{ padding: '6px 0', borderBottom: '1px solid rgba(27,27,25,.08)' }}>
                  <span style={{ fontFamily: MONO, fontSize: 11, letterSpacing: '0.1em', color: GOLD, marginRight: 8 }}>{p.project_code}</span>
                  <span style={{ fontSize: 13 }}>{projectsByCode.get(p.project_code)?.name || p.project_code}</span>
                  <div style={{ ...S.meta, fontSize: 11, marginTop: 2 }}>{p.chapter}: {p.reason}</div>
                </div>
              ))}
            <p style={{ ...S.body, marginTop: 10, fontSize: 12 }}>Tick a project to put it back. Fill its text or images in the press kit if the reason says they are missing.</p>
          </div>
        </div>
      </div>

      {showPreview && preview && (
        <div style={{ marginTop: 28 }}>
          <BlockTitle left="Preview" right={`${preview.pageCount} pages${preview.lowRes ? ` · ${preview.lowRes} image${preview.lowRes === 1 ? '' : 's'} still at thumbnail size` : ''}`} />
          <div ref={previewRef} style={{ width: '100%', background: '#EAE8E1', borderRadius: 4, padding: 10 }}>
            <iframe title="Deck preview" srcDoc={preview.html} sandbox="" style={{ width: '100%', height: preview.height, border: 'none', display: 'block' }} />
          </div>
        </div>
      )}

      {picker && pickerEntry && (
        <ImagePickerModal
          project={projectsByCode.get(picker.code) || { project_code: picker.code }}
          entry={pickerEntry}
          assets={projectAssets(picker.code, ranks, tags)}
          imagesPerProject={imagesPerProject}
          canEdit={canEdit}
          onSave={(hero, ids) => saveImages(picker.chapterId, picker.code, hero, ids)}
          onClose={() => setPicker(null)}
          onTagsChanged={refresh}
          showToast={showToast}
        />
      )}
    </div>
  );
}
